import {
    asyncLoadScript,
    includes,
    isEmpty,
    isNil,
    parseNumber,
    UPLOAD_FILE_COUNT
} from '@libs/shared';
import { jsSdkAuth, jsSdkFallback, sdkLog } from '../decorator';
import { IMAGE_SUPPORT_TYPE, JSSDK_URL, SdkResponseCode } from '../constant';
import {
    ScanType,
    SdkError,
    SDKFunction,
    SdkType,
    ShareLinkOptions,
    ShareLinkResult
} from '../interface';
import { processImageName, sdkLogger } from '../utils';
import JsSdkBase from '../core/js-sdk-base';

/**
 * 企业微信 jssdk
 */
export default class WxWorkSdk extends JsSdkBase {
    private readyCallback: (() => void) | undefined;
    private errorCallback: ((err: any) => void) | undefined;

    constructor() {
        super();
        this.sdkType = SdkType.WxWork;
    }

    /**
     * 加载企微sdk脚本
     */
    @sdkLog()
    loadScript() {
        return Promise.all([
            asyncLoadScript(JSSDK_URL.WxWork.Base),
            asyncLoadScript(JSSDK_URL.WxWork.Agent)
        ]);
    }

    @sdkLog()
    init(config: Parameters<JsSdkBase['init']>[0]) {
        return super.init(config);
    }

    /**
     * https://developer.work.weixin.qq.com/document/path/94325
     * @param config
     * @param agentConfig
     */
    @sdkLog()
    _config(config: JsSdkBase['jsSdkConfig'], agentConfig?: JsSdkBase['wxAgentJsSdkConfig']) {
        const hasAgentConfig = !isEmpty(agentConfig);
        const registerUrl = window.location.href.split('#')[0];
        const printSignatureUrlContext = (context: { prefix: string; requestedUrl: string }) => {
            sdkLogger.info(`${this.sdkType} ${context.prefix} url context`, {
                // 注册企微 SDK 时的页面 URL
                registerUrl,
                // 企微 SDK 请求签名的 URL
                requestedUrl: context.requestedUrl,
                // 此刻请求签名时的当前页面完整 URL
                currentUrl: window.location.href
            });
        };
        // iframe 内无法成功 register，不会响应任何回调
        window.ww?.register({
            corpId: config.appId,
            agentId: agentConfig?.agentid,
            jsApiList: [...(config.jsApiList || []), ...(agentConfig?.jsApiList || [])],
            /**
             * 注册sdk信息后，sdk内部会在需要的时候自动调用 config（比如页面url变化时）
             * @see https://fanruan-x.feishu.cn/wiki/SKsOwKrAYi6OhwkY0vwcAhk8nxd#share-WGZbdrkGcoSiEQxJq2HctwVgn3b
             * @param requestedUrl 企微sdk内部返回请求地址，企微sdk将用这个url去校验签名
             */
            getConfigSignature: async (requestedUrl: string) => {
                // 首次注册时复用已获取的签名，避免重复请求导致初始化鉴权失败
                const { jsSdkConfig } =
                    requestedUrl === registerUrl
                        ? { jsSdkConfig: config }
                        : await this.fetchConfig(requestedUrl);
                printSignatureUrlContext({
                    prefix: 'getConfigSignature',
                    requestedUrl
                });
                return {
                    timestamp: jsSdkConfig.timestamp,
                    nonceStr: jsSdkConfig.nonceStr,
                    signature: jsSdkConfig.signature
                };
            },
            /**
             * 注册sdk信息后，sdk内部会在需要的时候自动调用 agentConfig（比如页面url变化时）
             * @see https://fanruan-x.feishu.cn/wiki/SKsOwKrAYi6OhwkY0vwcAhk8nxd#share-WGZbdrkGcoSiEQxJq2HctwVgn3b
             * @param requestedUrl 企微sdk内部返回请求地址，企微将用这个url去校验签名
             */
            getAgentConfigSignature: isEmpty(agentConfig)
                ? undefined
                : async (requestedUrl: string) => {
                      printSignatureUrlContext({
                          prefix: 'getAgentConfigSignature',
                          requestedUrl
                      });
                      // 页面 URL 变化后重新获取对应的应用签名
                      const { wxAgentJsSdkConfig } =
                          requestedUrl === registerUrl
                              ? { wxAgentJsSdkConfig: agentConfig! }
                              : await this.fetchConfig(requestedUrl);
                      return {
                          timestamp: wxAgentJsSdkConfig.timestamp,
                          nonceStr: wxAgentJsSdkConfig.nonceStr,
                          signature: wxAgentJsSdkConfig.signature
                      };
                  },
            onConfigSuccess: () => {
                sdkLogger.info(`${this.sdkType} onConfigSuccess`);
                if (!hasAgentConfig) {
                    this.readyCallback?.();
                }
            },
            onConfigError: (err: any) => {
                this.errorCallback?.(
                    new SdkError(
                        this.sdkType,
                        SdkResponseCode.ThirdPartyServiceFail,
                        JSON.stringify(err)
                    )
                );
            },
            onAgentConfigSuccess: () => {
                sdkLogger.info(`${this.sdkType} onAgentConfigSuccess`);
            },
            onAgentConfigFail: (err: any) => {
                this.errorCallback?.(
                    new SdkError(
                        this.sdkType,
                        SdkResponseCode.ThirdPartyServiceFail,
                        JSON.stringify(err)
                    )
                );
            }
        });
        if (hasAgentConfig) {
            window.ww?.initOpenData({
                success: () => {
                    this.readyCallback?.();
                },
                fail: (err: any) => {
                    this.errorCallback?.(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        }
    }

    _ready(callback: () => void) {
        this.readyCallback = () => {
            sdkLogger.info(`${this.sdkType} ready callback`);
            callback();
        };
    }

    _error(callback: (err: any) => void) {
        this.errorCallback = (err: any) => {
            sdkLogger.error(`${this.sdkType} error callback`, { err });
            callback(err);
        };
    }

    /**
     * 预览图片
     * @see https://developer.work.weixin.qq.com/document/path/100609
     * @param urls 需要预览的图片链接列表
     * @param current 当前显示图片的链接
     */
    @jsSdkAuth
    @sdkLog()
    previewImage(urls: string[], current: string) {
        return new Promise((resolve, reject) => {
            window.ww?.previewImage({
                current: current, // 当前显示图片的http链接
                urls: urls, // 需要预览的图片http链接列表
                success: (result: any) => {
                    resolve(result);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 定位
     * @see https://developer.work.weixin.qq.com/document/path/100663
     */
    @jsSdkAuth
    @jsSdkFallback((originalArgs) => [{ ...originalArgs[0], noIpLocate: 0 }])
    @sdkLog()
    getLocation(): ReturnType<JsSdkBase['getLocation']> {
        return new Promise((resolve, reject) => {
            window.ww?.getLocation({
                type: 'gcj02',
                success: (result: any) => {
                    const lng = parseNumber(result.longitude);
                    const lat = parseNumber(result.latitude);
                    if (!isNil(lng) && !isNil(lat)) {
                        const position = [lng, lat];
                        resolve([position, result]);
                    } else {
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.Location.LngOrLatEmpty,
                                `longitude or latitude is empty, longitude: ${lng}, latitude: ${lat}`
                            )
                        );
                    }
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                /**
                 * 微信定位向用户请求权限若选择拒绝，会触发cancel回调，而不是fail回调
                 * @see https://code.fineres.com/projects/FX/repos/fx-code-mobile/pull-requests/5722/overview
                 */
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 扫码
     * @see https://developer.work.weixin.qq.com/document/path/90525
     * @param options 扫码参数
     */
    @jsSdkAuth
    @sdkLog()
    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        let scanType = ['qrCode', 'barCode'];
        switch (options.type) {
            case ScanType.QrCode:
                scanType = ['qrCode'];
                break;
            case ScanType.BarCode:
                scanType = ['barCode'];
                break;
        }
        return new Promise((resolve, reject) => {
            window.ww?.scanQRCode({
                needResult: true,
                scanType, // 可以指定扫二维码还是一维码
                success: (res: any) => {
                    let result = res.resultStr;
                    if (includes(['barCode', 'all'], options.type)) {
                        // 一维码去除前缀（一维码类型）
                        result = result.replace(
                            /^(CODABAR|AZTEC|CODE_(25|39|93|128)|EAN_(8|13)|ITF|MAXICODE|RSS_(14|EXPANDED)|UPC_(EAN_EXTENSION|A|E)),/,
                            ''
                        );
                    }
                    resolve(result);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 微信上传图片
     * @see https://developer.work.weixin.qq.com/document/path/100610
     * @param localId 图片ID
     */
    @jsSdkAuth
    @sdkLog()
    uploadImage(localId: string): Promise<string> {
        return new Promise((resolve, reject) => {
            window.ww?.uploadImage({
                localId: localId, // 需要上传的图片的本地ID，由chooseImage接口获得
                isShowProgressTips: true,
                success: (res: any) => {
                    const serverId = res.serverId; // 返回图片的服务器端ID
                    resolve(serverId);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 拍照或从手机相册中选图
     * @see https://developer.work.weixin.qq.com/document/path/100608
     * @param options 配置参数
     */
    @jsSdkAuth
    @sdkLog()
    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        const {
            compressed = false,
            onlyCamera = false,
            maxFileCount = UPLOAD_FILE_COUNT.SINGLE
        } = options;

        return new Promise<File[]>((resolve, reject) => {
            const sizeType = compressed ? ['compressed'] : ['original'];
            const sourceType = onlyCamera ? ['camera'] : ['album', 'camera'];
            const count = Math.min(maxFileCount, 9);
            window.ww?.chooseImage({
                count: count,
                sizeType: sizeType, // 可以指定是原图还是压缩图，默认二者都有
                sourceType: sourceType, // 可以指定来源是相册还是相机，默认二者都有
                success: async (res: any) => {
                    if (!compressed) {
                        /**
                         * 使用串行上传，否则微信可能上传失败
                         * 返回选定照片的本地ID列表，localId可以作为img标签的src属性显示图片
                         */
                        const serverIds: string[] = [];
                        const localIds = res.localIds;
                        for (const localId of localIds) {
                            const serverId = await this.uploadImage(localId);
                            serverIds.push(serverId);
                        }
                        resolve(serverIds as unknown as File[]);
                    } else {
                        /**
                         * 读取本地图片文件
                         */
                        const promises: Promise<File>[] = [];
                        for (const localId of res.localIds) {
                            const promise = new Promise<File>((resolve, reject) => {
                                /**
                                 * 获取本地图片接口
                                 * @see https://developer.work.weixin.qq.com/document/path/100612
                                 */
                                window.ww?.getLocalImgData({
                                    localId: localId, // 图片的localID
                                    success: (res: any) => {
                                        const localData = res.localData; // localData是图片的base64数据
                                        // base64 转 file
                                        let arr = localData.split(',');

                                        // 处理base64数据，确保有正确的MIME类型前缀 目前已知：安卓微信公众号环境下没有
                                        if (!localData.startsWith('data:')) {
                                            // 如果没有MIME类型前缀，添加默认的
                                            arr = [`data:image/jpeg;base64`, localData];
                                        }

                                        //  @see https://project.feishu.cn/b2rl2h/story/detail/6167478667
                                        // 优先根据 localId 后缀判断 originalType
                                        let originalType = '';
                                        const extMatch = (localId.match(/\.(jpg|jpeg|png|gif)$/i) ||
                                            [])[0];
                                        if (extMatch) {
                                            const typeMap: Record<string, string> = {
                                                '.jpeg': 'image/jpeg',
                                                '.jpg': 'image/jpeg',
                                                '.png': 'image/png',
                                                '.gif': 'image/gif'
                                            };
                                            originalType = typeMap[extMatch.toLowerCase()] || '';
                                        } else {
                                            const mimeMatch = arr[0].match(/:(.*?);/);
                                            // 如果无法匹配到MIME类型，使用默认值
                                            originalType = mimeMatch ? mimeMatch[1] : '';
                                        }

                                        // 如果无法匹配到MIME类型，使用默认值
                                        const mime = includes(IMAGE_SUPPORT_TYPE, originalType)
                                            ? originalType
                                            : 'image/jpeg';
                                        const bstr = atob(arr[1]);

                                        // 将二进制字符串转换为 Uint8Array
                                        const u8arr = new Uint8Array(bstr.length);
                                        for (let i = 0; i < bstr.length; i++) {
                                            u8arr[i] = bstr.charCodeAt(i);
                                        }

                                        // 创建 Blob 对象
                                        const blob = new Blob([u8arr], { type: mime });
                                        /**
                                         * localId ios和安卓存在差别，安卓：//xxx/xxx/1241231231.jpg ios:1241231231
                                         * 需要给ios增加正确的后缀
                                         */
                                        const rawFileName = localId.split('/').pop() || 'image';
                                        const fileName = processImageName(rawFileName, mime);

                                        // 创建 File 对象
                                        const file = new File([blob], fileName, { type: mime });

                                        resolve(file);
                                    },
                                    fail: (err: any) => {
                                        reject(
                                            new SdkError(
                                                this.sdkType,
                                                SdkResponseCode.ThirdPartyServiceFail,
                                                JSON.stringify(err)
                                            )
                                        );
                                    }
                                });
                            });
                            promises.push(promise);
                        }
                        const files = await Promise.all(promises);
                        resolve(files);
                    }
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 使用微信内置地图查看位置
     * @see https://developer.work.weixin.qq.com/document/path/100662
     * @param params 地图参数
     */
    @jsSdkAuth
    @sdkLog()
    openMap(params: Parameters<JsSdkBase['openMap']>[0]) {
        const { lng, lat, address } = params;
        return new Promise((resolve, reject) => {
            window.ww?.openLocation({
                latitude: lat,
                longitude: lng,
                name: address,
                scale: 16,
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 关闭当前网页窗口
     * @see https://developer.work.weixin.qq.com/document/path/100598
     */
    @jsSdkAuth
    @sdkLog()
    closeWindow() {
        return new Promise<void>((resolve, reject) => {
            window.ww?.closeWindow({
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    hasFunction(functionName: SDKFunction): Promise<boolean> {
        const functionList = [SDKFunction.ChooseImage];
        return Promise.resolve(functionList.includes(functionName));
    }

    /**
     * 打开链接
     * @see https://developer.work.weixin.qq.com/document/path/100602
     * @param url 目标地址
     */
    @jsSdkAuth
    @jsSdkFallback()
    @sdkLog()
    openLink(url: string) {
        return new Promise((resolve, reject) => {
            window.ww?.openDefaultBrowser({
                url,
                success: (res: any) => {
                    resolve(res);
                },
                fail: (res: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(res)
                        )
                    );
                }
            });
        });
    }

    /**
     * 企业微信自定义转发到会话
     * 允许自定义 title、desc、imgUrl。
     * 原生 Promise 成功返回 success，可识别的取消返回 cancelled，其他错误继续 reject。
     * 跨企业转发、转发到微信实测无回调，Promise 保持 pending。
     * @see https://developer.work.weixin.qq.com/document/path/100759
     */
    @jsSdkAuth
    @sdkLog()
    shareLink(options: ShareLinkOptions): Promise<ShareLinkResult> {
        return new Promise<ShareLinkResult>((resolve, reject) => {
            window.ww
                ?.shareAppMessage({
                    link: options.url,
                    title: options.title,
                    desc: options.description,
                    imgUrl: options.imageUrl
                })
                .then(
                    (res: any) => {
                        resolve({ status: 'success', data: res });
                    },
                    (err: any) => {
                        if (
                            err.errMsg === 'shareAppMessage:cancel' ||
                            err.err_msg === 'shareAppMessage:cancel'
                        ) {
                            resolve({ status: 'cancelled', data: err });
                            return;
                        }
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        );
                    }
                );
        });
    }
}
